"use client";

import { useState, type FormEvent } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import {
  CONTACT_TYPES,
  type ContactType,
} from "@/lib/contact/validate";

// Sprint 7 §2.8 microcopy (agent-default — flagged in handoff_2 for operator review).
const PRIVACY_NOTICE_PREFIX = "By submitting, you agree to our ";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "error"; message: string };

const TYPE_OPTIONS: { value: ContactType; label: string }[] = [
  { value: "partner", label: "Partner / Sponsor" },
  { value: "press", label: "Press / Media" },
  { value: "investor", label: "Investor" },
  { value: "general", label: "General" },
];

const inputClass =
  "w-full rounded-sm border border-line bg-base px-4 py-3 font-body text-base text-fg placeholder:text-fg-faint focus:border-gold focus:outline-none";

const labelClass =
  "block font-body text-sm uppercase tracking-wider text-fg-muted";

function isContactType(v: string): v is ContactType {
  return (CONTACT_TYPES as readonly string[]).includes(v);
}

export function ContactForm() {
  const searchParams = useSearchParams();
  const paramType = searchParams.get("type");
  const initialType: ContactType =
    paramType && isContactType(paramType) ? paramType : "general";
  const [type, setType] = useState<ContactType>(initialType);
  const [fields, setFields] = useState<Record<string, string>>({
    email: "",
    message: "",
    company: "",
    role: "",
    outlet: "",
    deadline: "",
    firm: "",
    aum: "",
    name: "",
  });
  // Honeypot — bots fill any named input; humans don't see this field.
  const [hp, setHp] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  function update(key: string) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFields((prev) => ({ ...prev, [key]: e.target.value }));
    };
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus({ kind: "submitting" });
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type,
          ...fields,
          _hp: hp,
          consentAt: new Date().toISOString(),
        }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (data.ok) {
        setStatus({ kind: "success" });
      } else {
        setStatus({ kind: "error", message: data.error ?? "Something went wrong." });
      }
    } catch {
      setStatus({ kind: "error", message: "Network error. Try again." });
    }
  }

  if (status.kind === "success") {
    return (
      <p
        role="status"
        className="rounded-sm border border-gold bg-elevated p-6 font-body text-base text-gold"
      >
        Thanks, your message is in. Check your inbox for a confirmation.
      </p>
    );
  }

  return (
    <form onSubmit={onSubmit} className="relative space-y-6">
      {/* Honeypot — bots fill any named input; off-screen, not tab-reachable. */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "-9999px",
          width: 1,
          height: 1,
          overflow: "hidden",
        }}
      >
        <label htmlFor="contact-hp-company">Company (do not fill)</label>
        <input
          id="contact-hp-company"
          type="text"
          name="_hp_company"
          tabIndex={-1}
          autoComplete="off"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="contact-type" className={labelClass}>
          What is this about?
        </label>
        <select
          id="contact-type"
          name="type"
          value={type}
          onChange={(e) => {
            const v = e.target.value;
            if (isContactType(v)) setType(v);
          }}
          className={`${inputClass} appearance-none`}
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="contact-email" className={labelClass}>
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={fields.email}
          onChange={update("email")}
          placeholder="you@example.com"
          className={`${inputClass} mt-2`}
        />
      </div>

      {type === "partner" && (
        <>
          <div>
            <label htmlFor="contact-company" className={labelClass}>
              Company
            </label>
            <input
              id="contact-company"
              type="text"
              name="company"
              required
              value={fields.company}
              onChange={update("company")}
              className={`${inputClass} mt-2`}
            />
          </div>
          <div>
            <label htmlFor="contact-role" className={labelClass}>
              Your role
            </label>
            <input
              id="contact-role"
              type="text"
              name="role"
              required
              value={fields.role}
              onChange={update("role")}
              className={`${inputClass} mt-2`}
            />
          </div>
        </>
      )}

      {type === "press" && (
        <>
          <div>
            <label htmlFor="contact-outlet" className={labelClass}>
              Outlet
            </label>
            <input
              id="contact-outlet"
              type="text"
              name="outlet"
              required
              value={fields.outlet}
              onChange={update("outlet")}
              className={`${inputClass} mt-2`}
            />
          </div>
          <div>
            <label htmlFor="contact-deadline" className={labelClass}>
              Deadline (optional)
            </label>
            <input
              id="contact-deadline"
              type="text"
              name="deadline"
              value={fields.deadline}
              onChange={update("deadline")}
              placeholder="e.g., Friday EOD ET"
              className={`${inputClass} mt-2`}
            />
          </div>
        </>
      )}

      {type === "investor" && (
        <>
          <div>
            <label htmlFor="contact-firm" className={labelClass}>
              Firm
            </label>
            <input
              id="contact-firm"
              type="text"
              name="firm"
              required
              value={fields.firm}
              onChange={update("firm")}
              className={`${inputClass} mt-2`}
            />
          </div>
          <div>
            <label htmlFor="contact-aum" className={labelClass}>
              AUM (optional)
            </label>
            <input
              id="contact-aum"
              type="text"
              name="aum"
              value={fields.aum}
              onChange={update("aum")}
              className={`${inputClass} mt-2`}
            />
          </div>
        </>
      )}

      {type === "general" && (
        <div>
          <label htmlFor="contact-name" className={labelClass}>
            Your name
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            autoComplete="name"
            required
            value={fields.name}
            onChange={update("name")}
            className={`${inputClass} mt-2`}
          />
        </div>
      )}

      <div>
        <label htmlFor="contact-message" className={labelClass}>
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={6}
          value={fields.message}
          onChange={update("message")}
          className={`${inputClass} mt-2`}
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-4">
          <Button
            variant="gold-filled"
            type="submit"
            disabled={status.kind === "submitting"}
          >
            {status.kind === "submitting" ? "Sending…" : "Send message"}
          </Button>
          {status.kind === "error" && (
            <p role="alert" className="font-body text-sm text-crimson">
              {status.message}
            </p>
          )}
        </div>
        <p className="font-body text-xs text-fg-faint">
          {PRIVACY_NOTICE_PREFIX}
          <Link
            href="/legal/privacy"
            className="underline-offset-2 hover:text-gold hover:underline"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
